import { Action } from 'redux';
import { put, select, call } from '@redux-saga/core/effects';

import { CheckoutActions, CheckoutActionTypes } from '../checkout.types';
import { ACTION_CREATOR_NAME, userHelper, analyticsHelper, errorToBasketErrorModel, logsHelper } from 'helpers';
import { AppState, ErrorAction } from '../../root.types';
import { api } from 'repositories/api';
import { loadBasketSaga } from '../../basket/actions';
import { BasketSagaReturnType, callBasketSaga } from 'store/sagaHelpers';
import { CredentialsModel } from 'models';
import { transferBasketSaga } from './transferBasket';
import { verifyEmailExistsSaga, VerifyEmailExistsResult } from './verifyEmailExists';

const LOGIN_FAIL_MESSAGES_FOR_ANALYTICS = {
    invalidCredentials: 'An incorrect email or password has been entered. Please try again',
    technicalError: "There's been an error signing into your account. Please try again later or call 03444 81 81 81 to speak to a sofologist"
};

export interface LoginProcessAction extends Action<CheckoutActionTypes.LOGIN_PROCESS> {
    email: string;
    password: string;
    path: string;
}

export interface LoginProcessActionStart extends Action<CheckoutActionTypes.LOGIN_PROCESS_START> {
}

export interface LoginProcessActionSuccess extends Action<CheckoutActionTypes.LOGIN_PROCESS_SUCCESS> {
}

export interface LoginProcessActionError extends ErrorAction<CheckoutActionTypes.LOGIN_PROCESS_ERROR> {
}

export interface LoginCredentialsErrorAction extends ErrorAction<CheckoutActionTypes.LOGIN_CREDENTIALS_ERROR> {
}

export const loginProcess: (email: string, password: string, path: string) => LoginProcessAction = (email: string, password: string, path: string) => ({
    type: CheckoutActionTypes.LOGIN_PROCESS,
    email,
    password,
    path
});
loginProcess[ACTION_CREATOR_NAME] = 'loginProcess';

export type LoginProcessFunc = (...args: Parameters<typeof loginProcess>) => void;

export function* loginProcessSaga({ email, password, path }: LoginProcessAction): BasketSagaReturnType {
    yield put<CheckoutActions>({
        type: CheckoutActionTypes.LOGIN_PROCESS_START
    });

    try {
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.CLEAR_LOGIN_AUTH_ERRORS
        });

        const emailCheck: VerifyEmailExistsResult = yield call(verifyEmailExistsSaga, email);

        if (!emailCheck.success) {
            sendLoginFailAnalytics(path, LOGIN_FAIL_MESSAGES_FOR_ANALYTICS.technicalError);
            yield put<CheckoutActions>({
                type: CheckoutActionTypes.CHECK_EMAIL_ERROR,
                error: errorToBasketErrorModel(
                    emailCheck.error || new Error('User does not exist'),
                    'verifyEmailExistsSaga'
                )
            });

            yield put<CheckoutActions>({
                type: CheckoutActionTypes.LOGIN_PROCESS_ERROR,
                error: errorToBasketErrorModel(
                    emailCheck.error || new Error('User does not exist'),
                    'loginProcessSaga'
                )
            });

            return false;
        }

        yield put<CheckoutActions>({
            type: CheckoutActionTypes.CHECK_EMAIL_SUCCESS,
            userExists: Boolean(emailCheck.userExists),
            email
        });

        if (!emailCheck.userExists) {
            sendLoginFailAnalytics(path, LOGIN_FAIL_MESSAGES_FOR_ANALYTICS.invalidCredentials);
            yield put<CheckoutActions>({
                type: CheckoutActionTypes.LOGIN_PROCESS_SUCCESS
            });

            return false;
        }

        const authResult = yield call(authenticateCustomerSaga, email, password, path);

        return authResult;
    } catch (error) {
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.LOGIN_PROCESS_ERROR,
            error: errorToBasketErrorModel(error, 'loginProcessSaga')
        });

        return false;
    }
}

export function* authenticateCustomerSaga(email: string, password: string, path: string): BasketSagaReturnType {
    try {
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.LOGIN_PROCESS_START
        });

        let userCredentials: CredentialsModel;
        try {
            userCredentials = yield api.hybris.getCredentials(email, password);
        } catch (credentialError) {
            sendLoginFailAnalytics(path, LOGIN_FAIL_MESSAGES_FOR_ANALYTICS.invalidCredentials);
            yield put<CheckoutActions>({
                type: CheckoutActionTypes.LOGIN_CREDENTIALS_ERROR,
                error: errorToBasketErrorModel(credentialError, 'loginProcessSaga')
            });

            return false;
        }

        if (userCredentials) {
            try {
                const token = userCredentials.tokenType + ' ' + userCredentials.accessToken;
                userCredentials.userId = yield api.account.getCustomerUserId(email, token);
            } catch (error) {
                // sometimes account api returns 404 instead of created user (usually after registration)
                logsHelper.error(error);
            }

            userHelper.saveCredentials(userCredentials, email);

            const state: AppState = yield select();
            const basketId = state.basketState.basket!.guid;

            yield callBasketSaga(transferBasketSaga, {
                type: CheckoutActionTypes.TRANSFER_BASKET,
                basketId
            });

            yield callBasketSaga(loadBasketSaga);

            if (!userCredentials.userId) {
                userCredentials.userId = state.basketState.basket!.session.userId;
                userHelper.saveCredentials(userCredentials, email);
            }

            userHelper.saveRetailExperienceUser(userCredentials.userId, email);

            sendAnalytics(userCredentials.userId);
            api.google.sendHashedEmail(email, 'Login');

            yield put<CheckoutActions>({
                type: CheckoutActionTypes.LOGIN_PROCESS_SUCCESS
            });
        }

        return true;
    } catch (error) {
        sendLoginFailAnalytics(path, LOGIN_FAIL_MESSAGES_FOR_ANALYTICS.technicalError);
        yield put<CheckoutActions>({
            type: CheckoutActionTypes.LOGIN_PROCESS_ERROR,
            error: errorToBasketErrorModel(error, 'loginProcessSaga')
        });

        return false;
    }
}

function sendAnalytics(userId: string): void {
    analyticsHelper.withLogging(() => {
        api.exponea.identify(userId);
    }, 'loginProcessSaga');
}

function sendLoginFailAnalytics(path: string, validationError: string): void {
    analyticsHelper.sendToGoogleLoginMessage(
        path,
        'user-login-fail',
        { 'validation-error': validationError }
    );
}